import { Button, Cascader, DatePicker, Input, InputNumber, Select } from 'antd'
import dayjs from 'dayjs'
import {
  ActionsListProps,
  COST_TYPE_HIERARCHY,
  CostTypeHierarchyDisplay,
} from 'features/fde'
import { Check, Edit3, Plus, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoAssetHealthAction, RolodexEntryType } from 'types'

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmptyActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border: 1px dashed ${({ theme }) => theme.forecast.secondaryText};
  border-radius: 8px;
  text-align: center;
`

const EmptyText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 14px;
  margin-bottom: 12px;
`

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 6px;
  font-size: 13px;
`

const ActionRowContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ActionName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 13px;
  line-height: 1.2;
`

const ActionDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`

const ActionDetailField = styled.div`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ActionField = styled.div`
  flex: 1;
  min-width: 0;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`

const EditingFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EditingRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const EditingActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.forecast.border2};
`

const StyledInput = styled(Input)`
  background-color: #363636;
  color: #ffffff;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 4px;
  font-size: 13px;
  height: 28px;

  &::placeholder {
    color: #898989;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    border-color: #ffffff;
  }
`

const StyledDatePicker = styled(DatePicker)`
  background-color: #363636;
  color: #ffffff;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 4px;
  font-size: 13px;

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    border-color: #ffffff;
  }

  &.ant-picker-focused {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    border-color: #ffffff !important;
  }

  .ant-picker-input > input {
    color: #ffffff;
  }
`

const StyledInputNumber = styled(InputNumber)`
  background-color: #363636;
  color: #ffffff;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 4px;
  font-size: 13px;
  width: 100%;

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    border-color: #ffffff;
  }

  &.ant-input-number-focused {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
    border-color: #ffffff !important;
  }

  .ant-input-number-input {
    color: #ffffff;
  }

  .ant-input-number-handler-wrap {
    background: #363636;
  }

  .ant-input-number-handler {
    border-left: 1px solid ${({ theme }) => theme.forecast.border2};
    color: #ffffff;

    &:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`

const AddActionForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.forecast.background3};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  margin-top: 8px;
`

const StyledSelect = styled(Select)`
  .ant-select-selector {
    background-color: #363636 !important;
    color: #ffffff !important;
    border: 1px solid ${({ theme }) => theme.forecast.border2} !important;
    border-radius: 4px !important;
    font-size: 13px !important;
    height: 28px !important;
  }

  &.ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2) !important;
    border-color: #ffffff !important;
  }
`

const StyledCascader = styled(Cascader)`
  .ant-select-selector {
    background-color: #363636 !important;
    color: #ffffff !important;
    border: 1px solid ${({ theme }) => theme.forecast.border2} !important;
    border-radius: 4px !important;
    font-size: 13px !important;
    height: 28px !important;
  }

  &.ant-select-focused .ant-select-selector,
  &.ant-cascader-focused .ant-select-selector {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2) !important;
    border-color: #ffffff !important;
  }

  .ant-select-selection-item {
    color: #ffffff !important;
  }
`

const ComponentDisplay = styled.div`
  padding: 8px 12px;
  background: #333333;
  border-radius: 4px;
  color: #ffffff;
  font-size: 12px;
  border: 1px solid #6b7280;
`
interface ActionFormFieldsProps {
  actionDate?: string
  actionCost?: number
  costTypeHierarchy?: string[]
  actionTags?: string[]
  deficiencyIds?: string[]
  onDateChange: (date: string | undefined) => void
  onCostChange: (cost: number | undefined) => void
  onCostTypeHierarchyChange: (hierarchy: string[] | undefined) => void
  onTagsChange: (tags: string[]) => void
  onDeficiencyIdsChange?: (ids: string[]) => void
  availableTags: string[]
  includeDeficiencies?: boolean
  deficiencyOptions?: Array<{ label: string; value: string }>
}

const ActionFormFields: React.FC<ActionFormFieldsProps> = ({
  actionDate,
  actionCost,
  costTypeHierarchy,
  actionTags,
  deficiencyIds,
  onDateChange,
  onCostChange,
  onCostTypeHierarchyChange,
  onTagsChange,
  onDeficiencyIdsChange,
  availableTags,
  includeDeficiencies,
  deficiencyOptions,
}) => {
  return (
    <>
      <EditingRow>
        <ActionField>
          <StyledDatePicker
            value={actionDate ? dayjs(actionDate) : null}
            onChange={(date) =>
              onDateChange(
                date && dayjs.isDayjs(date)
                  ? date.format('YYYY-MM-DD')
                  : undefined
              )
            }
            placeholder='Due date'
            style={{ width: '100%' }}
          />
        </ActionField>
        <ActionField>
          <StyledInputNumber
            value={actionCost}
            onChange={(value) =>
              onCostChange(typeof value === 'number' ? value : undefined)
            }
            placeholder='Cost (optional)'
            prefix='$'
          />
        </ActionField>
        <ActionField>
          <StyledCascader
            value={costTypeHierarchy}
            onChange={(value) => {
              if (value && value.length > 0) {
                onCostTypeHierarchyChange(value as string[])
              } else {
                onCostTypeHierarchyChange(undefined)
              }
            }}
            options={COST_TYPE_HIERARCHY}
            placeholder='Cost Type'
            changeOnSelect
            allowClear
            style={{ width: '100%' }}
            displayRender={(labels) => labels.join(' > ')}
          />
        </ActionField>
      </EditingRow>
      <EditingRow>
        <ActionField>
          <StyledSelect
            mode='tags'
            value={actionTags || []}
            onChange={(value) => onTagsChange(value as string[])}
            placeholder='Add tags...'
            options={availableTags.map((tag) => ({
              label: tag,
              value: tag,
            }))}
            style={{ width: '100%' }}
            tokenSeparators={[',']}
            maxTagCount='responsive'
          />
        </ActionField>
      </EditingRow>
      {includeDeficiencies && deficiencyOptions && onDeficiencyIdsChange && (
        <EditingRow>
          <ActionField>
            <StyledSelect
              mode='multiple'
              value={deficiencyIds || []}
              onChange={(value) => onDeficiencyIdsChange(value as string[])}
              placeholder='Link deficiencies...'
              options={deficiencyOptions}
              style={{ width: '100%' }}
              maxTagCount='responsive'
              allowClear
            />
          </ActionField>
        </EditingRow>
      )}
    </>
  )
}

export const ActionsList: React.FC<ActionsListProps> = ({
  actions,
  components,
  onActionEdit,
  onActionCreate,
  readonly,
  isLoading,
  availableTags = [],
  includeDeficiencies = false,
  deficiencies = [],
}) => {
  const [editingActionId, setEditingActionId] = useState<string | null>(null)
  const [editingValues, setEditingValues] = useState<
    Partial<GeckoAssetHealthAction['data']>
  >({})
  const [editingComponentId, setEditingComponentId] = useState<
    string | undefined
  >()
  const [editingDeficiencyIds, setEditingDeficiencyIds] = useState<
    string[] | undefined
  >()
  const [isCreating, setIsCreating] = useState(false)
  const [newAction, setNewAction] = useState<
    Partial<GeckoAssetHealthAction['data']>
  >({
    name: '',
    actionDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    actionCost: undefined,
    costTypeHierarchy: undefined,
    actionTags: [],
    deficiencyIds: [],
  })
  const [newActionComponentId, setNewActionComponentId] = useState<
    string | undefined
  >()
  const [newActionDeficiencyIds, setNewActionDeficiencyIds] = useState<
    string[]
  >([])

  const componentOptions = useMemo(() => {
    return components.map((component) => ({
      value: component.id,
      label: component.data.name,
    }))
  }, [components])

  const deficiencyOptions = useMemo(() => {
    return deficiencies.map((deficiency) => ({
      value: deficiency.id,
      label: deficiency.data.title,
    }))
  }, [deficiencies])

  const getComponentName = (componentId: string | null | undefined) => {
    if (!componentId) return '-'
    const component = components.find((c) => c.id === componentId)
    return component?.data.name || 'Unknown Component'
  }

  const getDeficiencyTitles = (deficiencyIds: string[] | undefined) => {
    if (!deficiencyIds || deficiencyIds.length === 0) return null
    const titles = deficiencyIds
      .map((id) => {
        const deficiency = deficiencies.find((d) => d.id === id)
        return deficiency?.data.title
      })
      .filter((title): title is string => !!title)
    return titles.length > 0 ? titles.join(', ') : null
  }

  const handleEdit = (actionId: string) => {
    const action = actions.find((a) => a.id === actionId)
    if (action) {
      setEditingActionId(actionId)
      setEditingValues({
        ...action.data,
        actionTags: action.data.actionTags || [],
        deficiencyIds: action.data.deficiencyIds || [],
      })
      setEditingComponentId(
        action.tags[RolodexEntryType.COMPONENT] || undefined
      )
      setEditingDeficiencyIds(action.data.deficiencyIds || [])
    }
  }

  const handleSave = async () => {
    if (!editingActionId || !editingValues.name?.trim()) return

    await onActionEdit(editingActionId, {
      name: editingValues.name,
      actionDate: editingValues.actionDate,
      actionCost: editingValues.actionCost,
      costTypeHierarchy: editingValues.costTypeHierarchy,
      actionTags: editingValues.actionTags || [],
      deficiencyIds: editingDeficiencyIds || [],
    })

    setEditingActionId(null)
    setEditingValues({})
    setEditingComponentId('')
    setEditingDeficiencyIds(undefined)
  }

  const handleCancel = () => {
    setEditingActionId(null)
    setEditingValues({})
    setEditingComponentId('')
    setEditingDeficiencyIds(undefined)
  }

  const handleCreate = async () => {
    if (!newAction.name?.trim()) return

    const actionData: Partial<GeckoAssetHealthAction['data']> & {
      componentId?: string
    } = {
      name: newAction.name,
      actionDate: newAction.actionDate,
      actionCost: newAction.actionCost,
      costTypeHierarchy: newAction.costTypeHierarchy,
      actionTags: newAction.actionTags || [],
      deficiencyIds: newActionDeficiencyIds || [],
    }

    // Add component ID if multiple components and one is selected
    if (components.length > 1 && newActionComponentId) {
      actionData.componentId = newActionComponentId
    }

    await onActionCreate(actionData)

    setNewAction({
      name: '',
      actionDate: undefined,
      actionCost: undefined,
      costTypeHierarchy: undefined,
      actionTags: [],
      deficiencyIds: [],
    })
    setNewActionComponentId('')
    setNewActionDeficiencyIds([])
    setIsCreating(false)
  }

  const handleCancelCreate = () => {
    setNewAction({
      name: '',
      actionDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
      actionCost: undefined,
      costTypeHierarchy: undefined,
      actionTags: [],
      deficiencyIds: [],
    })
    setNewActionComponentId(undefined)
    setNewActionDeficiencyIds([])
    setIsCreating(false)
  }

  const handleEditingNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValues({
      ...editingValues,
      name: e.target.value,
    })
  }

  const handleNewActionNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewAction({
      ...newAction,
      name: e.target.value,
    })
  }

  // Auto-select component if there's only one
  React.useEffect(() => {
    if (components.length === 1 && !newActionComponentId) {
      setNewActionComponentId(components[0].id)
    }
  }, [components, newActionComponentId])

  // Auto-select component for editing if there's only one and not already set
  React.useEffect(() => {
    if (components.length === 1 && editingActionId && !editingComponentId) {
      setEditingComponentId(components[0].id)
    }
  }, [components, editingActionId, editingComponentId])

  if (actions.length === 0 && !isCreating) {
    return (
      <ActionsContainer>
        <EmptyActionsContainer>
          <EmptyText>No actions defined</EmptyText>
          {!readonly && (
            <Button
              type='primary'
              size='small'
              icon={<Plus size={14} />}
              onClick={() => setIsCreating(true)}
              disabled={isLoading}>
              Add Action
            </Button>
          )}
        </EmptyActionsContainer>
      </ActionsContainer>
    )
  }

  return (
    <ActionsContainer>
      {actions.map((action) => (
        <ActionRow key={action.id}>
          {editingActionId === action.id ? (
            <EditingFields>
              <EditingRow>
                <ActionField>
                  <StyledInput
                    value={editingValues.name || ''}
                    onChange={handleEditingNameChange}
                    placeholder='Action name'
                  />
                </ActionField>
              </EditingRow>
              <EditingRow>
                {components.length > 1 ? (
                  <ActionField>
                    <StyledSelect
                      value={editingComponentId}
                      onChange={(value) =>
                        setEditingComponentId(value as string)
                      }
                      placeholder='Select component'
                      options={componentOptions}
                      style={{ width: '100%' }}
                    />
                  </ActionField>
                ) : components.length === 1 ? (
                  <ActionField>
                    <ComponentDisplay>
                      Component: {components[0].data.name}
                    </ComponentDisplay>
                  </ActionField>
                ) : null}
              </EditingRow>
              <ActionFormFields
                actionDate={editingValues.actionDate}
                actionCost={editingValues.actionCost}
                costTypeHierarchy={editingValues.costTypeHierarchy}
                actionTags={editingValues.actionTags}
                deficiencyIds={editingDeficiencyIds}
                onDateChange={(date) =>
                  setEditingValues({ ...editingValues, actionDate: date })
                }
                onCostChange={(cost) =>
                  setEditingValues({ ...editingValues, actionCost: cost })
                }
                onCostTypeHierarchyChange={(hierarchy) =>
                  setEditingValues({
                    ...editingValues,
                    costTypeHierarchy: hierarchy,
                  })
                }
                onTagsChange={(tags) =>
                  setEditingValues({ ...editingValues, actionTags: tags })
                }
                onDeficiencyIdsChange={(ids) => setEditingDeficiencyIds(ids)}
                availableTags={availableTags}
                includeDeficiencies={includeDeficiencies}
                deficiencyOptions={deficiencyOptions}
              />
              <EditingActions>
                <Button
                  size='small'
                  type='text'
                  icon={<Check size={12} />}
                  onClick={handleSave}
                  disabled={!editingValues.name?.trim()}
                />
                <Button
                  size='small'
                  type='text'
                  icon={<X size={12} />}
                  onClick={handleCancel}
                />
              </EditingActions>
            </EditingFields>
          ) : (
            <ActionRowContent>
              <ActionName>{action.data.name}</ActionName>
              <ActionDetails>
                <ActionDetailField>
                  {getComponentName(action.tags[RolodexEntryType.COMPONENT])}
                </ActionDetailField>
                <ActionDetailField>
                  {dayjs(action.data.actionDate).format('MMM YYYY')}
                </ActionDetailField>
                <ActionDetailField>
                  {action.data.actionCost
                    ? `$${action.data.actionCost.toLocaleString()}`
                    : '-'}
                </ActionDetailField>
                <ActionDetailField>
                  {action.data.costTypeHierarchy ? (
                    <CostTypeHierarchyDisplay
                      hierarchy={action.data.costTypeHierarchy}
                    />
                  ) : (
                    '-'
                  )}
                </ActionDetailField>
                {!readonly && (
                  <ActionButtons>
                    <Button
                      size='small'
                      type='text'
                      icon={<Edit3 size={14} />}
                      onClick={() => handleEdit(action.id)}
                      disabled={isLoading}
                    />
                  </ActionButtons>
                )}
              </ActionDetails>
              {includeDeficiencies &&
                getDeficiencyTitles(action.data.deficiencyIds) && (
                  <ActionDetailField
                    style={{
                      fontSize: '11px',
                      fontStyle: 'italic',
                      color: '#a0a0a0',
                      marginTop: '4px',
                    }}>
                    Deficiencies:{' '}
                    {getDeficiencyTitles(action.data.deficiencyIds)}
                  </ActionDetailField>
                )}
            </ActionRowContent>
          )}
        </ActionRow>
      ))}

      {isCreating && (
        <AddActionForm>
          <StyledInput
            value={newAction.name}
            onChange={handleNewActionNameChange}
            placeholder='Action name'
          />
          {components.length > 1 && (
            <StyledSelect
              value={newActionComponentId}
              onChange={(value) => setNewActionComponentId(value as string)}
              placeholder='Select component'
              options={componentOptions}
              style={{ width: '100%' }}
            />
          )}
          {components.length === 1 && (
            <ComponentDisplay>
              Component: {components[0].data.name}
            </ComponentDisplay>
          )}
          <ActionFormFields
            actionDate={newAction.actionDate}
            actionCost={newAction.actionCost}
            costTypeHierarchy={newAction.costTypeHierarchy}
            actionTags={newAction.actionTags}
            deficiencyIds={newActionDeficiencyIds}
            onDateChange={(date) =>
              setNewAction({ ...newAction, actionDate: date })
            }
            onCostChange={(cost) =>
              setNewAction({ ...newAction, actionCost: cost })
            }
            onCostTypeHierarchyChange={(hierarchy) =>
              setNewAction({ ...newAction, costTypeHierarchy: hierarchy })
            }
            onTagsChange={(tags) =>
              setNewAction({ ...newAction, actionTags: tags })
            }
            onDeficiencyIdsChange={(ids) => setNewActionDeficiencyIds(ids)}
            availableTags={availableTags}
            includeDeficiencies={includeDeficiencies}
            deficiencyOptions={deficiencyOptions}
          />
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              paddingTop: 8,
            }}>
            <Button type='primary' size='small' onClick={handleCreate}>
              Add Action
            </Button>
            <Button type='primary' size='small' onClick={handleCancelCreate}>
              Cancel
            </Button>
          </div>
        </AddActionForm>
      )}

      {!isCreating && !readonly && actions.length > 0 && (
        <Button
          type='primary'
          size='small'
          icon={<Plus size={14} />}
          onClick={() => setIsCreating(true)}
          disabled={isLoading}
          style={{ alignSelf: 'flex-start' }}>
          Add Action
        </Button>
      )}
    </ActionsContainer>
  )
}
